import { useState, useEffect, useCallback, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { 
  Leaf, 
  TreeDeciduous, 
  Archive, 
  LayoutDashboard,
  Plus,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Flower2,
  Menu,
  ListTodo,
  X,
  Wrench,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Logo } from '@/components/Logo';
import { MoodBooster } from '@/components/MoodBooster';

const SIDEBAR_COLLAPSED_KEY = 'yelv-sidebar-collapsed';
const SIDEBAR_WIDTH_EXPANDED = 224; // w-56 = 14rem = 224px
const SIDEBAR_WIDTH_COLLAPSED = 64; // w-16 = 4rem = 64px

// Simple store for sidebar state to share between components
let sidebarCollapsed = typeof window !== 'undefined' 
  ? localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  : false;
let listeners: Set<() => void> = new Set();

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot() {
  return sidebarCollapsed;
}

function setCollapsedState(value: boolean) {
  sidebarCollapsed = value;
  if (typeof window !== 'undefined') {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value));
  }
  listeners.forEach(listener => listener());
}

// Hook to get sidebar width for layout offset
export function useSidebarWidth() {
  const collapsed = useSyncExternalStore(subscribe, getSnapshot, () => false);
  // On mobile, sidebar is hidden so no offset needed
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (isMobile) return 0;
  return collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
}

interface NavItem {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

const navItems: NavItem[] = [
  { title: 'Garden', icon: Leaf, path: '/app' },
  { title: 'Dashboard', icon: LayoutDashboard, path: '/app/dashboard' },
  { title: 'Plan', icon: ListTodo, path: '/app/plan' },
  { title: 'Grove', icon: TreeDeciduous, path: '/app/grove' },
  { title: 'Withered', icon: Flower2, path: '/app/withered' },
  { title: 'Archived', icon: Archive, path: '/app/archived' },
  { title: 'Growth Instruments', icon: Wrench, path: '/app/growth-instruments' },
];

function DesktopSidebarContent({ collapsed, onCollapse }: { 
  collapsed: boolean; 
  onCollapse: (value: boolean) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();

  const isActive = (path: string) => {
    if (path === '/app/growth-instruments') {
      return location.pathname.startsWith('/app/growth-instruments');
    }
    if (path === '/app') {
      return location.pathname === '/app';
    }
    return location.pathname === path;
  };

  // Get avatar from Google auth metadata or profile
  const avatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture || profile?.avatar_url;
  
  // Generate initials fallback
  const getInitials = () => {
    if (profile?.display_name) {
      return profile.display_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return (user?.email?.charAt(0) || 'U').toUpperCase();
  };

  const handleNewSeed = () => {
    navigate('/app');
    window.dispatchEvent(new CustomEvent('yelv-focus-input'));
  };

  return (
    <>
      {/* Brand Header */}
      <div className={cn(
        "flex items-center h-[60px] shrink-0",
        collapsed ? "justify-center px-2" : "justify-between px-4"
      )}>
        {!collapsed && (
          <Logo size="md" className="text-foreground" />
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onCollapse(!collapsed)}
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>
      
      {/* Brand separator */}
      <div className="mx-3 border-b border-border/60" />

      {/* New Seed Action */}
      <div className="p-3 shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleNewSeed}
              className={cn(
                "w-full justify-start gap-2",
                collapsed && "justify-center px-0"
              )}
              size={collapsed ? "icon" : "default"}
            >
              <Plus className="h-4 w-4" />
              {!collapsed && <span>New Seed</span>}
            </Button>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right">
              New Seed
            </TooltipContent>
          )}
        </Tooltip>
      </div>

      {/* Navigation - scrollable area */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <Tooltip key={item.path}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'w-full justify-start gap-3 h-10',
                    collapsed && 'justify-center px-0',
                    active 
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium' 
                      : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
                  )}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  {!collapsed && <span>{item.title}</span>}
                </Button>
              </TooltipTrigger>
              {collapsed && (
                <TooltipContent side="right">
                  {item.title}
                </TooltipContent>
              )}
            </Tooltip>
          );
        })}
      </nav>

      {/* Footer - Mood Booster + User profile */}
      <div className="p-3 border-t border-border shrink-0 space-y-1">
        {/* Mood Booster */}
        <MoodBooster collapsed={collapsed} />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              onClick={() => navigate('/app/profile')}
              className={cn(
                'w-full justify-start gap-3 h-10',
                collapsed && 'justify-center px-0',
                location.pathname === '/app/profile' 
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium' 
                  : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
              )}
            >
              <Avatar className={cn(
                "h-6 w-6 flex-shrink-0",
                location.pathname === '/app/profile' && "ring-1 ring-primary"
              )}>
                <AvatarImage src={avatarUrl} alt={profile?.display_name || 'User'} />
                <AvatarFallback className="text-[10px] font-medium bg-primary/10 text-primary">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <span className="truncate text-xs">
                  {profile?.display_name || 'Profile'}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right">
              Profile
            </TooltipContent>
          )}
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              onClick={signOut}
              className={cn(
                'w-full justify-start gap-3 h-10 text-muted-foreground',
                collapsed && 'justify-center px-0'
              )}
            >
              <LogOut className="h-4 w-4 flex-shrink-0" />
              {!collapsed && <span className="text-xs">Sign out</span>}
            </Button>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right">
              Sign out
            </TooltipContent>
          )}
        </Tooltip>
      </div>
    </>
  );
}

function MobileDrawerContent({ onClose }: { onClose: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();

  const isActive = (path: string) => {
    if (path === '/app/growth-instruments') {
      return location.pathname.startsWith('/app/growth-instruments');
    }
    if (path === '/app') {
      return location.pathname === '/app';
    }
    return location.pathname === path;
  };

  // Get avatar from Google auth metadata or profile
  const avatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture || profile?.avatar_url;
  
  // Generate initials fallback
  const getInitials = () => {
    if (profile?.display_name) {
      return profile.display_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return (user?.email?.charAt(0) || 'U').toUpperCase();
  };

  const handleNewSeed = () => {
    navigate('/app');
    window.dispatchEvent(new CustomEvent('yelv-focus-input'));
    onClose();
  };

  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSignOut = () => {
    signOut();
    onClose();
  };

  return (
    <div 
      className="h-full flex flex-col bg-sidebar-background"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Brand Header with close button */}
      <div className="flex items-center justify-between h-[60px] px-4 shrink-0">
        <Logo size="md" className="text-foreground" />
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>
      
      {/* Brand separator */}
      <div className="mx-4 border-b border-border/60" />

      {/* New Seed Action */}
      <div className="px-4 py-4 shrink-0">
        <Button
          onClick={handleNewSeed}
          className="w-full justify-start gap-3 h-12"
        >
          <Plus className="h-5 w-5" />
          <span className="text-base">New Seed</span>
        </Button>
      </div>

      {/* Navigation - scrollable area */}
      <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <Button
              key={item.path}
              variant="ghost"
              onClick={() => handleNav(item.path)}
              className={cn(
                'w-full justify-start gap-4 h-12 text-base',
                active 
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium' 
                  : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
              )}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              <span>{item.title}</span>
            </Button>
          );
        })}
      </nav>

      {/* Footer - Mood Booster + User profile and sign out */}
      <div className="px-4 py-4 border-t border-border shrink-0 space-y-1">
        {/* Mood Booster */}
        <MoodBooster collapsed={false} />
        <Button
          variant="ghost"
          onClick={() => handleNav('/app/profile')}
          className={cn(
            'w-full justify-start gap-4 h-12 text-base',
            location.pathname === '/app/profile' 
              ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium' 
              : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
          )}
        >
          <Avatar className={cn(
            "h-7 w-7 flex-shrink-0",
            location.pathname === '/app/profile' && "ring-1 ring-primary"
          )}>
            <AvatarImage src={avatarUrl} alt={profile?.display_name || 'User'} />
            <AvatarFallback className="text-[11px] font-medium bg-primary/10 text-primary">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
          <span className="truncate">
            {profile?.display_name || 'Profile'}
          </span>
        </Button>
        <Button
          variant="ghost"
          onClick={handleSignOut}
          className="w-full justify-start gap-4 h-12 text-base text-muted-foreground"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          <span>Sign out</span>
        </Button>
      </div>
    </div>
  );
}

function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Lock body scroll when drawer is open
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalTop = document.body.style.top;
      const scrollY = window.scrollY;
      
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
        document.body.style.top = originalTop;
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="fixed inset-0 bg-black/40 z-50 md:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Drawer panel */}
      <div 
        className="fixed left-0 top-0 z-50 h-[100dvh] w-[min(85vw,320px)] bg-background shadow-xl md:hidden"
        style={{
          paddingLeft: 'env(safe-area-inset-left)',
        }}
      >
        <MobileDrawerContent onClose={onClose} />
      </div>
    </>
  );
}

export function AppSidebar() {
  const collapsed = useSyncExternalStore(subscribe, getSnapshot, () => false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobileDrawer = useCallback(() => {
    setMobileOpen(false);
  }, []);

  return (
    <>
      {/* Desktop sidebar - fixed position, hidden on mobile */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-screen flex-col border-r border-border bg-sidebar-background transition-all duration-300 z-40 hidden md:flex',
          collapsed ? 'w-16' : 'w-56'
        )}
      >
        <DesktopSidebarContent 
          collapsed={collapsed} 
          onCollapse={setCollapsedState}
        />
      </aside>

      {/* Mobile header with hamburger - only on mobile */}
      <div className="fixed top-0 left-0 right-0 h-14 border-b border-border bg-sidebar-background z-40 flex items-center px-4 md:hidden"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-10 w-10"
          onClick={() => setMobileOpen(true)}
        >
          <Menu className="h-5 w-5" />
        </Button>
        <Logo size="md" className="ml-3 text-foreground" />
      </div>

      {/* Mobile drawer */}
      <MobileDrawer open={mobileOpen} onClose={closeMobileDrawer} />

      {/* Mobile spacer for fixed header */}
      <div 
        className="md:hidden shrink-0" 
        style={{ 
          height: 'calc(56px + env(safe-area-inset-top, 0px))' 
        }} 
      />
    </>
  );
}
